import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../utils/api';
import { Eye, EyeOff, Loader2, Mail, Phone, ArrowLeft } from 'lucide-react';

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const phoneRegex = /^[6-9]\d{9}$/;

const Login = () => {
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();

  // 'password' | 'otp'
  const [method, setMethod] = useState('password');
  // OTP sub-mode: 'email' | 'phone'
  const [otpMode, setOtpMode] = useState('email');
  // OTP step: 'input' | 'verify'
  const [otpStep, setOtpStep] = useState('input');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = (fields) => {
    const errs = {};
    if (fields.includes('email') && !emailRegex.test(email.trim())) errs.email = 'Enter a valid email';
    if (fields.includes('password') && !password) errs.password = 'Password is required';
    if (fields.includes('phone') && !phoneRegex.test(phone.trim())) errs.phone = 'Enter a valid 10-digit mobile number';
    if (fields.includes('otp') && !/^\d{6}$/.test(otp)) errs.otp = 'Enter a valid 6-digit OTP';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ——— Password Login ———
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!validate(['email', 'password'])) return;
    setLoading(true);
    try {
      const response = await api.post('/auth/login', { email: email.trim(), password });
      loginWithToken(response.data.token, response.data.user);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Login failed';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  // ——— OTP: Send ———
  const handleSendOtp = async (e) => {
    e.preventDefault();
    const fields = otpMode === 'email' ? ['email'] : ['phone'];
    if (!validate(fields)) return;
    setLoading(true);
    try {
      if (otpMode === 'email') {
        await api.post('/auth/email/send-otp', { email: email.trim() });
      } else {
        await api.post('/auth/phone/send-otp', { phone: `+91${phone.trim()}` });
      }
      toast.success(`OTP sent to your ${otpMode}`);
      setOtpStep('verify');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  // ——— OTP: Verify ———
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!validate(['otp'])) return;
    setLoading(true);
    try {
      let response;
      if (otpMode === 'email') {
        response = await api.post('/auth/email/verify-otp', { email: email.trim(), otp: otp });
      } else {
        response = await api.post('/auth/phone/verify-otp', { phone: `+91${phone.trim()}`, code: otp });
      }
      loginWithToken(response.data.token, response.data.user);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      setErrors({ otp: error.response?.data?.detail || 'Verification failed' });
    } finally {
      setLoading(false);
    }
  };

  // ——— Google Login ———
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/google/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&app_name=${encodeURIComponent('The Vastram Store')}`;
  };

  const resetOtp = () => {
    setOtpStep('input');
    setOtp('');
    setErrors({});
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 bg-transparent border text-sm text-forest focus:outline-none transition-colors ${
      errors[field] ? 'border-red-400 focus:border-red-500' : 'border-forest/20 focus:border-gold'
    }`;

  const tabClass = (active) =>
    `flex-1 py-3 text-sm uppercase tracking-[0.1em] transition-all border-b-2 ${
      active ? 'text-gold border-gold font-medium' : 'text-forest/50 border-transparent hover:text-forest'
    }`;

  return (
    <div className="min-h-screen flex items-center justify-center bg-ivory px-4 py-12" data-testid="login-page">
      <div className="w-full max-w-md">
        <h1 className="font-heading text-3xl text-forest mb-2 text-center">Welcome Back</h1>
        <p className="text-forest/60 text-sm mb-6 text-center">Sign in to your Vastram account</p>

        {/* Google Login Button */}
        <button onClick={handleGoogleLogin} className="w-full border border-forest/20 py-3 text-sm text-forest hover:bg-forest/5 transition-colors flex items-center justify-center gap-3 mb-6" data-testid="google-login-button">
          <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" /><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" /><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" /><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" /></svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 border-t border-forest/10"></div>
          <span className="text-forest/40 text-xs uppercase tracking-widest">or</span>
          <div className="flex-1 border-t border-forest/10"></div>
        </div>

        {/* Method Tabs */}
        <div className="flex mb-6 border-b border-forest/10" data-testid="login-method-tabs">
          <button onClick={() => { setMethod('password'); resetOtp(); }} className={tabClass(method === 'password')} data-testid="tab-password">
            Password
          </button>
          <button onClick={() => { setMethod('otp'); setOtpStep('input'); setErrors({}); }} className={tabClass(method === 'otp')} data-testid="tab-otp">
            Login with OTP
          </button>
        </div>

        {/* ——— PASSWORD TAB ——— */}
        {method === 'password' && (
          <form onSubmit={handlePasswordLogin} className="space-y-4" data-testid="password-login-form">
            <div>
              <input type="email" placeholder="Email Address" value={email} onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors(p => ({...p, email: ''})); }}
                className={inputClass('email')} data-testid="login-email-input" />
              {errors.email && <p className="text-red-500 text-xs mt-1" data-testid="email-error">{errors.email}</p>}
            </div>
            <div>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} placeholder="Password" value={password} onChange={(e) => { setPassword(e.target.value); if (errors.password) setErrors(p => ({...p, password: ''})); }}
                  className={inputClass('password')} data-testid="login-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-forest/40 hover:text-forest" tabIndex={-1}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && <p className="text-red-500 text-xs mt-1" data-testid="password-error">{errors.password}</p>}
            </div>
            <div className="text-right">
              <Link to="/forgot-password" className="text-xs text-gold hover:underline" data-testid="forgot-password-link">Forgot Password?</Link>
            </div>
            <button type="submit" disabled={loading} className="w-full bg-forest text-ivory py-3 text-sm uppercase tracking-[0.15em] hover:bg-forest/90 transition-colors disabled:opacity-50 flex items-center justify-center" data-testid="login-submit-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign In'}
            </button>
          </form>
        )}

        {/* ——— OTP TAB ——— */}
        {method === 'otp' && otpStep === 'input' && (
          <div data-testid="otp-login-section">
            {/* Email / Phone toggle */}
            <div className="flex gap-3 mb-5">
              <button onClick={() => { setOtpMode('email'); setErrors({}); }}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 border text-sm transition-all ${otpMode === 'email' ? 'border-gold text-gold bg-gold/5' : 'border-forest/15 text-forest/50 hover:text-forest'}`}
                data-testid="otp-mode-email">
                <Mail className="w-4 h-4" /> Email
              </button>
              <button onClick={() => { setOtpMode('phone'); setErrors({}); }}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 border text-sm transition-all ${otpMode === 'phone' ? 'border-gold text-gold bg-gold/5' : 'border-forest/15 text-forest/50 hover:text-forest'}`}
                data-testid="otp-mode-phone">
                <Phone className="w-4 h-4" /> Phone
              </button>
            </div>
            <form onSubmit={handleSendOtp} className="space-y-4">
              {otpMode === 'email' ? (
                <div>
                  <input type="email" placeholder="Email Address" value={email} onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors(p => ({...p, email: ''})); }}
                    className={inputClass('email')} data-testid="otp-email-input" />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
              ) : (
                <div>
                  <div className="flex">
                    <span className="px-3 py-3 border border-r-0 border-forest/20 text-forest/60 text-sm bg-forest/5">+91</span>
                    <input type="tel" placeholder="Phone Number" value={phone} onChange={(e) => { setPhone(e.target.value.replace(/\D/g, '').slice(0, 10)); if (errors.phone) setErrors(p => ({...p, phone: ''})); }}
                      className={`flex-1 px-4 py-3 bg-transparent border text-sm text-forest focus:outline-none transition-colors ${errors.phone ? 'border-red-400' : 'border-forest/20 focus:border-gold'}`}
                      data-testid="otp-phone-input" />
                  </div>
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>
              )}
              <button type="submit" disabled={loading} className="w-full bg-forest text-ivory py-3 text-sm uppercase tracking-[0.15em] hover:bg-forest/90 transition-colors disabled:opacity-50 flex items-center justify-center" data-testid="send-otp-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send OTP'}
              </button>
            </form>
          </div>
        )}

        {method === 'otp' && otpStep === 'verify' && (
          <div data-testid="otp-verify-section">
            <button onClick={resetOtp} className="flex items-center text-forest/60 hover:text-forest text-sm mb-4 transition-colors" data-testid="back-to-otp-input">
              <ArrowLeft className="w-4 h-4 mr-1" /> Change {otpMode === 'email' ? 'email' : 'phone'}
            </button>
            <p className="text-forest/60 text-sm mb-4">
              OTP sent to <strong>{otpMode === 'email' ? email : `+91 ${phone}`}</strong>
            </p>
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div>
                <input type="text" placeholder="Enter 6-digit OTP" value={otp} onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); if (errors.otp) setErrors(p => ({...p, otp: ''})); }}
                  maxLength={6}
                  className={`w-full px-4 py-3 bg-transparent border text-center text-2xl tracking-[0.5em] font-mono text-forest focus:outline-none transition-colors ${errors.otp ? 'border-red-400' : 'border-forest/20 focus:border-gold'}`}
                  data-testid="login-otp-input" autoFocus />
                {errors.otp && <p className="text-red-500 text-xs mt-1" data-testid="otp-error">{errors.otp}</p>}
              </div>
              <button type="submit" disabled={loading} className="w-full bg-forest text-ivory py-3 text-sm uppercase tracking-[0.15em] hover:bg-forest/90 transition-colors disabled:opacity-50 flex items-center justify-center" data-testid="verify-otp-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & Sign In'}
              </button>
            </form>
          </div>
        )}

        <p className="text-center text-sm text-forest/60 mt-6">
          Don't have an account?{' '}
          <Link to="/register" className="text-gold hover:underline" data-testid="register-link">Create Account</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
